import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useCallback, useState } from "react";

import type {
  NotificationCutoverConfigFieldsFragment,
  NotificationEngineMode,
  NotificationEventType,
} from "../../generated/graphql";
import NotificationCutoverDetails from "./notification-cutover-details";
import {
  deliveryChipColor,
  deliveryLabel,
  deliveryReasonText,
  effectiveDeliveryState,
  isInertFull,
  modeOptions,
} from "./notification-cutover-format";

const styles = {
  select: { minWidth: 260 },
  eventCell: { verticalAlign: "top" },
  detailCell: { py: 0, borderBottom: "none" },
  toggleCell: { width: 48 },
  summary: { maxWidth: 420 },
} satisfies Record<string, SxProps<Theme>>;

type NotificationCutoverRowProps = {
  readonly config: NotificationCutoverConfigFieldsFragment;
  readonly saving: boolean;
  /**
   * The mode of this event's required partner event, when it has one and that
   * partner's row is on the page. Null otherwise.
   */
  readonly requiredPartnerMode?: NotificationEngineMode | null;
  readonly onChangeMode: (
    eventType: NotificationEventType,
    mode: NotificationEngineMode,
  ) => void;
};

/**
 * One event row: its identifier and one-line summary, whether it delivers to
 * anyone today, and its cutover mode, over an expandable panel carrying the
 * full description.
 *
 * `mode` and `fullEligible` are deliberately rendered together. Reported alone,
 * a stored Full on an ineligible event reads as a completed cutover while the
 * engine is actually running it as Shadow.
 */
const NotificationCutoverRow = ({
  config,
  saving,
  onChangeMode,
  requiredPartnerMode,
}: NotificationCutoverRowProps) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setExpanded((previous) => !previous);
  }, []);

  // Neither of these is memoised: `config` is a fresh object after every
  // refetch, so a `useMemo` keyed on it recomputes anyway, and the change
  // handler is called from an inline arrow the Select recreates each render.
  const options = modeOptions({ ...config, requiredPartnerMode });
  const inertFull = isInertFull(config);
  // The verdict the operator reads has to account for this row's own mode: the
  // registry state is static across environments, so a dormant event flipped to
  // Full still reports dormancy until the live mode is folded in.
  const deliveryState = effectiveDeliveryState({
    deliveryState: config.metadata.deliveryState,
    mode: config.mode,
    fullEligible: config.fullEligible,
  });

  return (
    <>
      <TableRow>
        <TableCell sx={styles.toggleCell}>
          <IconButton
            size="small"
            aria-expanded={expanded}
            aria-label={`${expanded ? "Hide" : "Show"} details for ${
              config.eventType
            }`}
            onClick={toggleExpanded}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={styles.eventCell}>
          <Typography variant="body2" fontWeight={500}>
            {config.eventType}
          </Typography>
          <Typography
            color="text.secondary"
            variant="body2"
            sx={styles.summary}
          >
            {config.metadata.description}
          </Typography>
        </TableCell>
        <TableCell sx={styles.eventCell}>
          <Tooltip
            title={deliveryReasonText(
              {
                deliveryState: config.metadata.deliveryState,
                mode: config.mode,
                fullEligible: config.fullEligible,
              },
              config.metadata.deliveryStateReason,
            )}
          >
            <Chip
              size="small"
              variant="outlined"
              color={deliveryChipColor(deliveryState)}
              label={deliveryLabel(deliveryState)}
            />
          </Tooltip>
        </TableCell>
        <TableCell sx={styles.eventCell}>
          <Stack spacing={0.5}>
            <Select<NotificationEngineMode>
              size="small"
              sx={styles.select}
              value={config.mode}
              disabled={saving}
              onChange={(event) => {
                onChangeMode(
                  config.eventType,
                  event.target.value as NotificationEngineMode,
                );
              }}
            >
              {options.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {inertFull ? (
              <Typography color="warning.main" variant="caption">
                Stored as Full but running as Shadow; no cutover is in effect.
              </Typography>
            ) : null}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={styles.detailCell} colSpan={4}>
          <Collapse unmountOnExit in={expanded} timeout="auto">
            <NotificationCutoverDetails config={config} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default NotificationCutoverRow;
